import { generateEntries } from "src/pages/Reports/Report/Report.utils";

type Orientation = "portrait" | "landscape";

const EXCLUDE_SELECTOR = "[data-pdf-exclude]";
const LANDSCAPE_MIN_COLUMNS = 8;
const LANDSCAPE_MIN_WIDTH = 1400;
const SMALL_FONT_MIN_COLUMNS = 13;
const FONT_FAMILY =
  'Arial, "Liberation Sans", "DejaVu Sans", Helvetica, sans-serif';

const getTables = (element: HTMLElement) =>
  Array.from(element.querySelectorAll("table")).filter(
    table => !table.closest(EXCLUDE_SELECTOR),
  );

const getColumnCount = (table: HTMLTableElement) =>
  Array.from(table.rows).reduce(
    (max, row) =>
      Math.max(
        max,
        Array.from(row.cells).reduce((sum, cell) => sum + cell.colSpan, 0),
      ),
    0,
  );

// Column widths as rendered on screen, taken from a row without merged cells.
const getColumnRatios = (table: HTMLTableElement, columnCount: number) => {
  const row = Array.from(table.rows).find(
    r =>
      r.cells.length === columnCount &&
      Array.from(r.cells).every(cell => cell.colSpan === 1),
  );
  if (!row) return null;

  const widths = Array.from(row.cells).map(
    cell => cell.getBoundingClientRect().width,
  );
  const total = widths.reduce((sum, width) => sum + width, 0);
  if (total <= 0 || widths.some(width => width <= 0)) return null;

  return widths.map(width => (width / total) * 100);
};

const createStyle = (fontSize: string) => {
  const style = document.createElement("style");
  style.textContent = `
    * { font-family: ${FONT_FAMILY} !important; box-sizing: border-box; }
    table { page-break-inside: auto !important; }
    thead { display: table-header-group; }
    tr { break-inside: avoid; page-break-inside: avoid; }
    th, td { vertical-align: middle; padding: 4px 6px; white-space: normal !important; overflow-wrap: anywhere; word-break: break-word; }
    th *, td * { font-size: ${fontSize} !important; white-space: normal !important; min-width: 0 !important; max-width: 100% !important; }
    th, th * { font-weight: 700 !important; }
  `;
  return style;
};

export const getSnapshotPdf = (
  element: HTMLDivElement,
): { body: string; orientation: Orientation } => {
  const measurements = getTables(element).map(table => {
    const columnCount = getColumnCount(table);
    return {
      columnCount,
      width: table.scrollWidth,
      ratios: getColumnRatios(table, columnCount),
    };
  });

  const maxColumns = Math.max(0, ...measurements.map(m => m.columnCount));
  const maxWidth = Math.max(0, ...measurements.map(m => m.width));
  const orientation: Orientation =
    maxColumns >= LANDSCAPE_MIN_COLUMNS || maxWidth > LANDSCAPE_MIN_WIDTH
      ? "landscape"
      : "portrait";
  const fontSize = maxColumns >= SMALL_FONT_MIN_COLUMNS ? "9px" : "11px";

  const copy = element.cloneNode(true) as HTMLDivElement;
  copy.querySelectorAll(`${EXCLUDE_SELECTOR}, .sort-indicator`).forEach(el => {
    el.remove();
  });

  generateEntries(copy);

  copy.querySelectorAll<HTMLElement>("[style]").forEach(el => {
    if (el.style.position === "sticky") {
      el.style.removeProperty("position");
      el.style.removeProperty("top");
      el.style.removeProperty("z-index");
    }
    el.style.removeProperty("overflow");
    el.style.removeProperty("overflow-x");
  });

  copy.querySelectorAll<HTMLElement>("th, td").forEach(cell => {
    cell.style.removeProperty("min-width");
    cell.style.removeProperty("max-width");
    cell.style.removeProperty("width");
    cell.style.setProperty("font-size", fontSize, "important");
  });

  getTables(copy).forEach((table, index) => {
    const ratios = measurements[index]?.ratios;
    if (!table.querySelector("colgroup") && ratios) {
      const colgroup = document.createElement("colgroup");
      ratios.forEach(ratio => {
        const col = document.createElement("col");
        col.style.width = `${ratio}%`;
        colgroup.appendChild(col);
      });
      table.prepend(colgroup);
    }

    table.style.width = "100%";
    table.style.minWidth = "0";
    table.style.fontSize = fontSize;
    table.style.removeProperty("page-break-inside");
    if (table.querySelector("colgroup")) table.style.tableLayout = "fixed";
  });

  copy.prepend(createStyle(fontSize));

  return { body: copy.innerHTML, orientation };
};
